import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useParams } from "react-router-dom";

import CancelIcon from "assets/icons/CancelIcon";
import LoadingIcon from "assets/icons/LoadingIcon";
import ArrowLeftIcon from "assets/icons/ArrowLeftIcon";
import ArrowRightIcon from "assets/icons/ArrowRightIcon";
import Portal from "components/Portal";
import type { Picture } from "types/Pictures";
import useAppDispatch from "hooks/useAppDispatch";
import { setSuccessStatus } from "features/success/successSlice";
import {
  updateIsFavoriteStatus,
  updatePictureWithFile,
} from "features/picture/pictureAction";
import { setErrorStatus } from "features/error/errorSlice";
import { FaBookmark } from "react-icons/fa";
import { CiBookmark } from "react-icons/ci";
import {
  HiOutlineDotsHorizontal,
  HiOutlineEye,
  HiOutlineEyeOff,
} from "react-icons/hi";
import { isFulfilled } from "@reduxjs/toolkit";
import useAppSelector from "hooks/useAppSelector";
import useAllClasses from "hooks/useAllClasses";
import { formatDate } from "utilities/DateFormatter";
import { PictureType, SupportedLang, StudentSortValue } from "config/constants";
import PublishStatusToggle from "./PublishStatusToggle";
import ClassPictureButton from "./ClassPictureButton";
import ActivityTagContainer from "../ActivityTagContainer";
import ImageEditor from "../../Pictures/ImageEditor";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  getPictureConversionStatus,
  getPictureAIStatus,
} from "services/pictureServices";
import TaggedStudentMarkers from "./TaggedStudentMarkers";
import TagStudentPopover from "./TagStudentPopover";
import TagStudentManagementPanel from "./TagStudentManagementPanel";
import useTagStudents from "./useTagStudents";
import { getStudents } from "features/student/studentAction";

// Utility function to clamp zoom values within bounds
const clampZoom = (
  zoom: number,
  min: number = 1,
  max: number = 3.0,
): number => {
  return Math.min(Math.max(zoom, min), max);
};

const ZOOM_DEBOUNCE_MS = 100;

// How far the pointer may drift between press and release and still count as a
// tag click rather than a pan. Only applied while the image is zoomed in.
const TAG_CLICK_DRAG_TOLERANCE_PX = 12;

// Cursor shown over the photo while tagging is enabled: the same green dot
// used for placed tag markers, instead of the generic "+" crosshair.
const TAG_TARGET_CURSOR =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18'><circle cx='9' cy='9' r='6' fill='%2310b981' stroke='white' stroke-width='2'/></svg>\") 9 9, crosshair";

// Memoized zoom control component with simplified scaling
const MemoizedZoomControl = React.memo(
  ({
    currentZoom,
    onZoomChange,
    disabled,
  }: {
    currentZoom: number;
    onZoomChange: (zoom: number) => void;
    disabled: boolean;
  }) => {
    const [sliderValue, setSliderValue] = useState(currentZoom);
    const sliderValueRef = useRef(currentZoom);

    // Update slider when zoom changes externally
    useEffect(() => {
      setSliderValue(currentZoom);
      sliderValueRef.current = currentZoom;
    }, [currentZoom]);

    const handleSliderChange = useCallback(
      (value: number | number[]) => {
        const zoom = Array.isArray(value) ? value[0] : value;
        const clampedZoom = clampZoom(zoom);
        setSliderValue(clampedZoom);
        sliderValueRef.current = clampedZoom;
        onZoomChange(clampedZoom);
      },
      [onZoomChange],
    );

    return (
      <div className="bg-black/50 rounded-full px-3 py-1 hover:bg-black/70 transition-colors">
        <div className="flex items-center space-x-2 min-w-[120px]">
          <span className="text-white text-xs">1x</span>
          <Slider
            min={1}
            max={3.0}
            step={0.1}
            value={sliderValue}
            onChange={handleSliderChange}
            disabled={disabled}
            className="flex-1"
            styles={{
              rail: {
                backgroundColor: "rgba(255,255,255,0.3)",
                height: 4,
                borderRadius: 2,
              },
              track: {
                backgroundColor: disabled ? "rgba(255,255,255,0.3)" : "white",
                height: 4,
                borderRadius: 2,
              },
              handle: {
                backgroundColor: "white",
                border: "2px solid #333",
                width: 16,
                height: 16,
                marginTop: -6,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                opacity: disabled ? 0.5 : 1,
              },
            }}
          />
          <span className="text-white text-xs">3.0x</span>
        </div>
      </div>
    );
  },
);

interface PictureDialogProps {
  picture: Picture;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
  onRefreshPictures?: () => void;
  allowTagging?: boolean;
}

type ProcessingStatus = "conversion" | "ai";

const PictureDialog: React.FC<PictureDialogProps> = ({
  picture,
  onPrevious,
  onNext,
  onClose,
  onRefreshPictures,
  allowTagging = true,
}) => {
  const { t } = useTranslation();
  const { classes } = useAllClasses();
  const { students } = useAppSelector((state) => state.student);
  const dispatch = useAppDispatch();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  const [hoveredDirection, setHoveredDirection] = useState<
    "previous" | "next" | null
  >(null);
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);
  const [processingStatus, setProcessingStatus] =
    useState<ProcessingStatus | null>(null);
  const [areTagMarkersVisible, setAreTagMarkersVisible] = useState(true);
  const [pendingCoordinates, setPendingCoordinates] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [highlightedTagId, setHighlightedTagId] = useState<number | null>(null);
  // Set when a student is picked from the management panel instead of the
  // on-photo popover; the next photo click tags them directly at that point.
  const [armedStudentId, setArmedStudentId] = useState<number | null>(null);
  const [pictureDimensions, setPictureDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [transformState, setTransformState] = useState({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });

  const isTaggingEnabled =
    allowTagging && picture.picture_type !== PictureType.class;
  const shouldRunAIPolling = picture.picture_type === PictureType.ai;

  const {
    taggedStudents,
    isLoadingTags,
    addingStudentId,
    removingTagId,
    addTag,
    removeTag,
  } = useTagStudents(picture, students, isTaggingEnabled, onRefreshPictures);

  // Students still taggable on this photo — drives the over-photo picker.
  const availableStudentsForTagging = useMemo(() => {
    const taggedIds = new Set(
      taggedStudents.map((tag) =>
        typeof tag.student_id === "string"
          ? Number.parseInt(tag.student_id, 10)
          : tag.student_id,
      ),
    );
    return students.filter(
      (student) => !taggedIds.has(student.id) && !student.is_ng,
    );
  }, [students, taggedStudents]);

  // Refs for transform control
  const transformRef = useRef<any>(null);
  const zoomUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentZoomRef = useRef(currentZoom);
  const isComponentMountedRef = useRef(true);
  const conversionPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const aiPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tagPointerStartRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  // Handle image loading with proper cleanup
  const displayImageUrl = editedImageUrl || picture.converted_picture;

  useEffect(() => {
    const image = new Image();
    image.src = displayImageUrl;
    setIsLoading(true);

    const handleImageLoad = () => {
      setImageAspectRatio(image.naturalWidth / image.naturalHeight);
      setIsLoading(false);
      setPictureDimensions({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };

    const handleImageError = () => {
      console.error("Failed to load image:", displayImageUrl);
      setIsLoading(false);
    };

    if (image.complete) {
      handleImageLoad();
    } else {
      image.onload = handleImageLoad;
      image.onerror = handleImageError;
    }

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [displayImageUrl]);

  // Reset zoom when picture changes
  useEffect(() => {
    setCurrentZoom(1);
    currentZoomRef.current = 1;
    setImageAspectRatio(null);
    setPendingCoordinates(null);
    setArmedStudentId(null);
    setHighlightedTagId(null);
    setAreTagMarkersVisible(true);
    // setPictureDimensions({ width: 0, height: 0 });
    setTransformState({ scale: 1, positionX: 0, positionY: 0 });

    // Reset transform component state
    if (transformRef.current?.resetTransform) {
      transformRef.current.resetTransform();
    }
  }, [picture.id]);

  // Cleanup timeout and mark component as unmounted
  useEffect(() => {
    return () => {
      if (zoomUpdateTimeoutRef.current) {
        clearTimeout(zoomUpdateTimeoutRef.current);
      }
      if (conversionPollingIntervalRef.current) {
        clearInterval(conversionPollingIntervalRef.current);
        conversionPollingIntervalRef.current = null;
      }
      if (aiPollingIntervalRef.current) {
        clearInterval(aiPollingIntervalRef.current);
        aiPollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    isComponentMountedRef.current = true;
    return () => {
      isComponentMountedRef.current = false;
    };
  }, []);

  // Keep currentZoomRef in sync
  useEffect(() => {
    currentZoomRef.current = currentZoom;
  }, [currentZoom]);

  // Debounced zoom change handler
  const handleZoomChange = useCallback((zoom: number) => {
    const clampedZoom = clampZoom(zoom);
    setCurrentZoom(clampedZoom);
    currentZoomRef.current = clampedZoom;
  }, []);

  // Debounced centerView function
  const debouncedCenterView = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (zoom: number) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (transformRef.current?.centerView && isComponentMountedRef.current) {
          transformRef.current.centerView(
            clampZoom(zoom),
            300,
            "easeInOutQuad",
          );
        }
      }, ZOOM_DEBOUNCE_MS);
    };
  }, []);

  // Debounced zoom update from transform
  const handleTransformChange = useCallback(
    (
      _: any,
      state: { scale: number; positionX: number; positionY: number },
    ) => {
      if (!isComponentMountedRef.current) return;

      setTransformState({
        scale: state.scale,
        positionX: state.positionX,
        positionY: state.positionY,
      });

      if (zoomUpdateTimeoutRef.current) {
        clearTimeout(zoomUpdateTimeoutRef.current);
      }

      zoomUpdateTimeoutRef.current = setTimeout(() => {
        if (!isComponentMountedRef.current) return;

        // Use more precise rounding and clamp the value
        const roundedZoom = clampZoom(Math.round(state.scale * 100) / 100);
        const currentZoomValue = currentZoomRef.current;

        if (Math.abs(roundedZoom - currentZoomValue) > 0.01) {
          setCurrentZoom(roundedZoom);
          currentZoomRef.current = roundedZoom;
        }
      }, 50); // 50ms debounce
    },
    [], // Remove currentZoom dependency to prevent recreation
  );

  // Conversion status polling function
  const pollConversionStatus = useCallback(async () => {
    if (!isComponentMountedRef.current) return false;

    try {
      const { done } = await getPictureConversionStatus(picture.id);

      if (done) {
        console.log("Image conversion completed");

        if (conversionPollingIntervalRef.current) {
          clearInterval(conversionPollingIntervalRef.current);
          conversionPollingIntervalRef.current = null;
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking conversion status:", error);
      return false;
    }
  }, [picture.id]);

  const completeEditSave = useCallback(() => {
    if (!isComponentMountedRef.current) {
      return;
    }

    setProcessingStatus(null);

    dispatch(
      setSuccessStatus({
        title: "_published_picture._edit_success",
        message: "_published_picture._edit_success_content",
        isDeleted: false,
      }),
    );

    if (onRefreshPictures) {
      onRefreshPictures();
    }
  }, [dispatch, onRefreshPictures]);

  const pollAIStatus = useCallback(async () => {
    if (!isComponentMountedRef.current) return false;

    try {
      const { done } = await getPictureAIStatus(picture.id);

      if (done) {
        console.log("AI processing completed");

        if (aiPollingIntervalRef.current) {
          clearInterval(aiPollingIntervalRef.current);
          aiPollingIntervalRef.current = null;
        }

        completeEditSave();

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking AI status:", error);
      return false;
    }
  }, [completeEditSave, picture.id]);

  const getImageCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (
        !containerRef.current ||
        pictureDimensions.width === 0 ||
        pictureDimensions.height === 0
      ) {
        return null;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const viewportX = clientX - rect.left;
      const viewportY = clientY - rect.top;

      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const imageAspectRatio =
        pictureDimensions.width / pictureDimensions.height;
      const containerAspectRatio = containerWidth / containerHeight;

      let imageDisplayWidth: number;
      let imageDisplayHeight: number;
      let offsetX: number;
      let offsetY: number;

      if (imageAspectRatio > containerAspectRatio) {
        imageDisplayWidth = containerWidth;
        imageDisplayHeight = containerWidth / imageAspectRatio;
        offsetX = 0;
        offsetY = (containerHeight - imageDisplayHeight) / 2;
      } else {
        imageDisplayWidth = containerHeight * imageAspectRatio;
        imageDisplayHeight = containerHeight;
        offsetX = (containerWidth - imageDisplayWidth) / 2;
        offsetY = 0;
      }

      const {
        scale = 1,
        positionX = 0,
        positionY = 0,
      } = transformRef.current?.state || transformState;

      const normalizedX =
        (viewportX - offsetX - positionX) / scale / imageDisplayWidth;
      const normalizedY =
        (viewportY - offsetY - positionY) / scale / imageDisplayHeight;

      if (
        normalizedX < 0 ||
        normalizedX > 1 ||
        normalizedY < 0 ||
        normalizedY > 1
      ) {
        return null;
      }

      return {
        x: normalizedX * pictureDimensions.width,
        y: normalizedY * pictureDimensions.height,
      };
    },
    [pictureDimensions.height, pictureDimensions.width, transformState],
  );

  useEffect(() => {
    if (!isTaggingEnabled) {
      setPendingCoordinates(null);
      setArmedStudentId(null);
    }
  }, [isTaggingEnabled]);

  const handleImagePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isTaggingEnabled) {
        return;
      }
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest("[data-tag-ignore-click]")) {
        return;
      }

      tagPointerStartRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
      };
    },
    [isTaggingEnabled],
  );

  const handleImagePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const pointerStart = tagPointerStartRef.current;
      tagPointerStartRef.current = null;

      if (!isTaggingEnabled || !pointerStart) {
        return;
      }
      if (pointerStart.pointerId !== event.pointerId) return;

      const target = event.target as HTMLElement;
      if (target.closest("[data-tag-ignore-click]")) return;

      // Only treat the gesture as a drag when the image is actually pannable
      // (zoomed in). At scale 1 there is nothing to pan, so any press-release
      // is a tag placement. The tolerance is generous because a normal mouse
      // or trackpad click drifts several pixels — a tight threshold made the
      // click silently do nothing, with no marker and no roster.
      const isPannable =
        (transformRef.current?.state?.scale ?? transformState.scale) > 1.01;
      if (isPannable) {
        const movement = Math.hypot(
          event.clientX - pointerStart.clientX,
          event.clientY - pointerStart.clientY,
        );
        if (movement > TAG_CLICK_DRAG_TOLERANCE_PX) return;
      }

      const coords = getImageCoordinates(event.clientX, event.clientY);
      if (!coords) return;

      const clampedCoords = {
        x: Math.max(0, Math.min(pictureDimensions.width, coords.x)),
        y: Math.max(0, Math.min(pictureDimensions.height, coords.y)),
      };
      setHighlightedTagId(null);

      // Student picked from the management panel: skip the popover and tag
      // them directly at this point.
      if (armedStudentId !== null) {
        const studentId = armedStudentId;
        setArmedStudentId(null);
        void addTag(studentId, clampedCoords);
        return;
      }

      setPendingCoordinates(clampedCoords);
    },
    [
      addTag,
      armedStudentId,
      getImageCoordinates,
      isTaggingEnabled,
      pictureDimensions.height,
      pictureDimensions.width,
      transformState.scale,
    ],
  );

  const handleSelectStudentForTag = useCallback(
    async (studentId: number) => {
      if (!isTaggingEnabled || !pendingCoordinates) {
        return;
      }
      setHighlightedTagId(null);
      const succeeded = await addTag(studentId, pendingCoordinates);
      if (succeeded) {
        setPendingCoordinates(null);
      }
    },
    [addTag, isTaggingEnabled, pendingCoordinates],
  );

  const handleCancelPendingTag = useCallback(() => {
    setPendingCoordinates(null);
  }, []);

  const handleArmStudentForTag = useCallback((studentId: number) => {
    setPendingCoordinates(null);
    setArmedStudentId(studentId);
  }, []);

  const handleCancelArmedTag = useCallback(() => {
    setArmedStudentId(null);
  }, []);

  const startAIPolling = useCallback(() => {
    if (!isComponentMountedRef.current) return;

    setProcessingStatus("ai");

    if (aiPollingIntervalRef.current) {
      clearInterval(aiPollingIntervalRef.current);
      aiPollingIntervalRef.current = null;
    }

    pollAIStatus().then((done) => {
      if (!done && isComponentMountedRef.current) {
        aiPollingIntervalRef.current = setInterval(() => {
          pollAIStatus();
        }, 2000);
      }
    });
  }, [pollAIStatus]);

  // Start conversion status polling
  const startConversionPolling = useCallback(() => {
    if (!isComponentMountedRef.current) return;

    setProcessingStatus("conversion");

    if (conversionPollingIntervalRef.current) {
      clearInterval(conversionPollingIntervalRef.current);
      conversionPollingIntervalRef.current = null;
    }

    pollConversionStatus().then((done) => {
      if (!isComponentMountedRef.current) return;

      if (done) {
        if (shouldRunAIPolling) {
          startAIPolling();
        } else {
          completeEditSave();
        }
        return;
      }

      conversionPollingIntervalRef.current = setInterval(() => {
        pollConversionStatus().then((conversionDone) => {
          if (!isComponentMountedRef.current) {
            return;
          }

          if (conversionDone) {
            if (conversionPollingIntervalRef.current) {
              clearInterval(conversionPollingIntervalRef.current);
              conversionPollingIntervalRef.current = null;
            }
            if (shouldRunAIPolling) {
              startAIPolling();
            } else {
              completeEditSave();
            }
          }
        });
      }, 2000);
    });
  }, [
    completeEditSave,
    pollConversionStatus,
    shouldRunAIPolling,
    startAIPolling,
  ]);

  // Handle edit save with proper API flow
  const handleEditSave = async (file: File) => {
    setIsSavingEdit(true);

    try {
      // Step 1: Wait for the update API call to finish
      const updateAction = await dispatch(
        updatePictureWithFile({
          id: picture.id,
          file: file,
        }),
      );

      if (isFulfilled(updateAction)) {
        const updatedPicture = updateAction.payload.picture;
        setEditedImageUrl(updatedPicture.converted_picture);
        setIsEditMode(false);

        // Step 2: Start polling conversion status after update is complete
        // (Success message will be shown only when polling completes)
        startConversionPolling();
      } else {
        dispatch(setErrorStatus("_published_picture._edit_error"));
      }
    } catch (error) {
      console.error("Error updating picture:", error);
      dispatch(setErrorStatus("_published_picture._edit_error"));
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
  };

  const handleClose = () => {
    // Clean up polling when dialog closes
    if (conversionPollingIntervalRef.current) {
      clearInterval(conversionPollingIntervalRef.current);
      conversionPollingIntervalRef.current = null;
    }
    if (aiPollingIntervalRef.current) {
      clearInterval(aiPollingIntervalRef.current);
      aiPollingIntervalRef.current = null;
    }
    setProcessingStatus(null);
    onClose();
  };

  const updateIsFavorite = async () => {
    const updateAction = await dispatch(
      updateIsFavoriteStatus({
        id: picture.id,
        formData: { is_favorite: !picture.is_favorite },
      }),
    );
    if (!isFulfilled(updateAction)) {
      dispatch(setErrorStatus(""));
    }
  };

  const renderClassName = (classId: number) => {
    const currentClass = classes.find((c) => c.id === classId);
    if (!currentClass) return "";
    return `${currentClass.year} - ${currentClass.name}`;
  };

  const { id } = useParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
  // excludes `students`: getStudents.pending resets students to a new []
  // reference, so depending on it here would re-trigger this effect and
  // dispatch getStudents in an infinite loop.
  const callFetchStudents = useCallback(() => {
    if (
      students.length === 0 ||
      students[0]?.class_id !== parseInt(id || "")
    ) {
      dispatch(
        getStudents({
          classId: parseInt(id || ""),
          sortValue: StudentSortValue.name_asc,
        }),
      );
    }
  }, [dispatch, id]);
  useEffect(() => {
    // Also re-verify when tagging flips on (e.g. a class photo just got
    // converted to individual): callFetchStudents no-ops if the roster is
    // already loaded for this class, so this is just a safety net against
    // the roster being stale or missing right when it's first needed.
    callFetchStudents();
  }, [callFetchStudents, isTaggingEnabled]);

  const handleToggleMarkerVisibility = useCallback(() => {
    setAreTagMarkersVisible((prev) => {
      if (prev) {
        setHighlightedTagId(null);
        setPendingCoordinates(null);
      }
      return !prev;
    });
  }, []);

  return (
    <Portal>
      <div className="fixed z-[99] top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center p-3 sm:p-4">
        <div className="w-full h-full max-h-[calc(100vh-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-h-[calc(100vh-2rem)] sm:max-w-[calc(100vw-2rem)] bg-white z-[100] rounded-lg shadow-lg relative overflow-hidden">
          <div className="h-full max-h-full flex flex-col overflow-y-auto p-2 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(280px,30%)] lg:gap-4 lg:overflow-hidden lg:p-4 relative">
          {/* Mobile header */}
          <div className="lg:hidden w-full flex flex-row-reverse items-center pb-2 transition duration-200">
            <button className="cursor-pointer" onClick={handleClose}>
              <CancelIcon width={32} height={32} color={"black"} />
            </button>
          </div>

          {/* Image viewer */}
          <div className="w-full min-w-0 flex flex-col relative">
            <div className="absolute right-4 top-4 lg:right-4 lg:top-4 z-40 pointer-events-auto">
              <button
                data-tag-ignore-click
                onClick={updateIsFavorite}
                className="rounded-full bg-white p-2 shadow-md hover:bg-gray-100 transition-colors relative group"
              >
                {picture.is_favorite ? (
                  <FaBookmark className="text-[#EA6E7D]" size={24} />
                ) : (
                  <CiBookmark className="text-[#767676]" size={24} />
                )}
                <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 w-max">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                  {t("_published_picture._favorite")}
                </div>
              </button>
            </div>

            <div className="flex-1 min-h-[200px] h-[50vh] sm:h-[60vh] lg:h-full bg-[#fcfcfc] relative overflow-hidden border-b border-[#eee] lg:border-b-0 lg:border-e">
              {processingStatus && (
                <div className="absolute inset-0 bg-black/50 z-40 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4 shadow-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="text-base text-gray-700 font-medium">
                      {processingStatus === "conversion"
                        ? t("_published_picture._converting_image")
                        : t("_published_picture._ai_processing", {
                            defaultValue: "AI振り分け中",
                          })}
                    </span>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                  <div className="w-32">
                    <LoadingIcon />
                  </div>
                </div>
              )}

              <div
                id="photo-content"
                ref={containerRef}
                onPointerDownCapture={handleImagePointerDown}
                onPointerUpCapture={handleImagePointerUp}
                onPointerCancelCapture={() => {
                  tagPointerStartRef.current = null;
                }}
                role="presentation"
                className="w-full h-full flex items-center justify-center relative overflow-hidden cursor-crosshair transition"
                style={
                  isTaggingEnabled ? { cursor: TAG_TARGET_CURSOR } : undefined
                }
              >
                {isTaggingEnabled && pendingCoordinates && (
                  <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-emerald-600/90 px-3 py-1 text-xs font-semibold text-white shadow">
                    {t("_unpublished_picture_class._position_selected", {
                      defaultValue:
                        "Position selected – choose a student to tag.",
                    })}
                  </div>
                )}
                <div
                  id="control-overlay"
                  data-tag-ignore-click
                  className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center space-x-2"
                >
                  {isTaggingEnabled && (
                    <button
                      id="toggle-visibility-button"
                      type="button"
                      data-tag-ignore-click
                      onClick={handleToggleMarkerVisibility}
                      aria-pressed={areTagMarkersVisible}
                      aria-label="タグ一時非表示"
                      className="relative group text-white outline-none bg-black/50 rounded-full p-1 hover:bg-black/70 transition-colors"
                    >
                      {areTagMarkersVisible ? (
                        <HiOutlineEyeOff className="h-5 w-5" />
                      ) : (
                        <HiOutlineEye className="h-5 w-5" />
                      )}
                      <div className="pointer-events-none absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800" />
                        タグ一時非表示
                      </div>
                    </button>
                  )}
                  <MemoizedZoomControl
                    currentZoom={currentZoom}
                    onZoomChange={(zoom) => {
                      handleZoomChange(zoom);
                      debouncedCenterView(zoom);
                    }}
                    disabled={isLoading || isSavingEdit}
                  />
                  <button
                    id="edit-button"
                    data-tag-ignore-click
                    onClick={() => setIsEditMode(true)}
                    className="bg-black/50 rounded-full p-1 hover:bg-black/70 transition-colors relative group"
                  >
                    <HiOutlineDotsHorizontal size={24} color="white" />
                    <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 w-max">
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                      {t("_published_picture._edit")}
                    </div>
                  </button>
                </div>

                <TransformWrapper
                  ref={transformRef}
                  initialScale={1}
                  minScale={1}
                  maxScale={3.0}
                  centerZoomedOut={true}
                  smooth={true}
                  wheel={{
                    step: 0.1,
                    smoothStep: 0.005,
                    activationKeys: [],
                  }}
                  pinch={{
                    step: 0.1,
                    disabled: false,
                  }}
                  doubleClick={{
                    mode: "toggle",
                    step: 1,
                    animationTime: 300,
                    animationType: "easeInOutQuad",
                  }}
                  alignmentAnimation={{
                    sizeX: 0,
                    sizeY: 0,
                    animationTime: 300,
                    animationType: "easeInOutQuad",
                  }}
                  velocityAnimation={{
                    equalToMove: true,
                    animationTime: 300,
                    animationType: "easeInOutQuad",
                  }}
                  onTransformed={handleTransformChange}
                >
                  <TransformComponent
                    wrapperClass="!w-full !h-full !flex !items-center !justify-center"
                    contentClass="!flex !items-center !justify-center !w-full !h-full"
                    wrapperStyle={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      className="max-w-full max-h-full object-contain select-none"
                      style={{
                        visibility: !isLoading ? "visible" : "hidden",
                        maxWidth: "100%",
                        maxHeight: "100%",
                      }}
                      src={displayImageUrl}
                      alt=""
                      onLoad={(e) => {
                        setIsLoading(false);
                        const img = e.target as HTMLImageElement;
                        setPictureDimensions({
                          width: img.naturalWidth,
                          height: img.naturalHeight,
                        });
                        if (!imageAspectRatio) {
                          setImageAspectRatio(
                            img.naturalWidth / img.naturalHeight,
                          );
                        }
                      }}
                      onError={() => {
                        setIsLoading(false);
                        console.error("Failed to load image:", displayImageUrl);
                      }}
                      draggable={false}
                    />
                  </TransformComponent>
                </TransformWrapper>

                <TaggedStudentMarkers
                  containerRef={containerRef}
                  pictureDimensions={pictureDimensions}
                  transformState={transformState}
                  taggedStudents={
                    isTaggingEnabled && areTagMarkersVisible
                      ? taggedStudents
                      : []
                  }
                  students={students}
                  highlightedTagId={
                    isTaggingEnabled && areTagMarkersVisible
                      ? highlightedTagId
                      : null
                  }
                  pendingCoordinates={
                    isTaggingEnabled && areTagMarkersVisible
                      ? pendingCoordinates
                      : null
                  }
                  onRemoveTag={(tagId) => {
                    void removeTag(tagId);
                  }}
                />

                {isTaggingEnabled && (
                  <TagStudentPopover
                    containerRef={containerRef}
                    pictureDimensions={pictureDimensions}
                    transformState={transformState}
                    pendingCoordinates={pendingCoordinates}
                    availableStudents={availableStudentsForTagging}
                    awaitingStudentId={addingStudentId}
                    onSelect={handleSelectStudentForTag}
                    onClose={handleCancelPendingTag}
                  />
                )}

                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                  {onPrevious && (
                    <div className="absolute top-1/2 left-5 -translate-y-1/2 z-20 pointer-events-auto">
                      <button
                        data-tag-ignore-click
                        className="rounded-full transition-colors"
                        style={{
                          backgroundColor:
                            hoveredDirection === "previous"
                              ? "rgba(0, 0, 0, 0.7)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                        onClick={onPrevious}
                        onMouseEnter={() => setHoveredDirection("previous")}
                        onMouseLeave={() => setHoveredDirection(null)}
                      >
                        <ArrowLeftIcon width={32} height={32} color={"white"} />
                      </button>
                    </div>
                  )}
                  {onNext && (
                    <div className="absolute top-1/2 right-5 -translate-y-1/2 z-20 pointer-events-auto">
                      <button
                        data-tag-ignore-click
                        className="rounded-full transition-colors"
                        style={{
                          backgroundColor:
                            hoveredDirection === "next"
                              ? "rgba(0, 0, 0, 0.7)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                        onClick={onNext}
                        onMouseEnter={() => setHoveredDirection("next")}
                        onMouseLeave={() => setHoveredDirection(null)}
                      >
                        <ArrowRightIcon
                          width={32}
                          height={32}
                          color={"white"}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Metadata panel */}
          <div className="w-full min-w-0 overflow-x-hidden flex flex-col gap-4 pt-4 lg:pt-0 transition duration-200">
            <div className="hidden lg:flex justify-end">
              <button className="cursor-pointer" onClick={handleClose}>
                <CancelIcon width={32} height={32} color={"black"} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 lg:pr-0 pb-4 flex flex-col">
              <div className="flex-1 space-y-4">
                <div className="space-y-3 lg:space-y-4">
                  <div className="space-y-1 lg:space-y-2">
                    <h2 className="text-lg lg:text-2xl font-medium">
                      {renderClassName(picture.class_id)}
                    </h2>
                    <p className="text-sm lg:text-base text-gray-600">
                      {`${t(
                        "_unpublished_picture_class._upload_date",
                      )} ${formatDate(new Date(picture.create_time), {
                        [SupportedLang.ja]: "yo Mo do",
                        [SupportedLang.en]: "MMMM d, yyyy",
                      })}`}
                    </p>
                  </div>
                  <div className="md:flex items-center justify-between">
                    <PublishStatusToggle picture={picture} />
                    <ClassPictureButton picture={picture} />
                  </div>
                </div>

                {isTaggingEnabled && (
                  <TagStudentManagementPanel
                    taggedStudents={taggedStudents}
                    availableStudents={availableStudentsForTagging}
                    isLoadingTags={isLoadingTags}
                    removingTagId={removingTagId}
                    armedStudentId={armedStudentId}
                    onArmStudent={handleArmStudentForTag}
                    onCancelArmedStudent={handleCancelArmedTag}
                    onRemoveTag={(tagId) => {
                      void removeTag(tagId);
                    }}
                  />
                )}
              </div>
              <div className="mt-4">
                <ActivityTagContainer pictures={[picture]} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditMode && (
        <ImageEditor
          imageUrl={picture.converted_picture}
          onSave={handleEditSave}
          onCancel={handleEditCancel}
          isUploading={isSavingEdit}
          pictureId={picture.id}
        />
      )}
      </div>
    </Portal>
  );
};

export default PictureDialog;
